import { InboxOutlined } from '@ant-design/icons';
import { Modal, Form, Input, Select, InputNumber, Upload, Result } from 'antd';
import { useFileUpload } from 'hooks';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { FC, memo, useEffect, useState } from 'react';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import {
  fetchAppointmentById,
  updateAppointmentDoctorPayment
} from 'services/api/appointments';
import { AppointmentDetail } from 'types/api/appointments';

import { showErrorMessage } from 'utils/showErrorMessage';
import { Loading } from 'components/ui';
import { getFileUrl } from 'utils/file';

const { TextArea } = Input;

const PaymentStatusModal: FC = () => {
  // form
  const [form] = Form.useForm();
  // context
  const { paymentStatus } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();
  const [showFileList, setShowFileList] = useState(true);
  const [percent, setPercent] = useState(0);
  const [appointment, setAppointment] = useState<AppointmentDetail | null>(
    null
  );

  const [isFetching, setIsFetching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { upload, setResult, onCancel, result } = useFileUpload({
    handleResult: true,
    loading: true
  });

  // Modal is rendered globally in App.tsx (not inside the
  // AppointmentsDetails route tree). Fetch the appointment on
  // visible-edge ourselves since there's no context to read from.
  useEffect(() => {
    const id = paymentStatus.params?.id;

    if (!paymentStatus?.visible || !id) {
      setAppointment(null);

      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setIsFetching(true);
        const res = await fetchAppointmentById(id);
        if (!cancelled) setAppointment(res);
      } catch {
        if (!cancelled) setAppointment(null);
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [paymentStatus?.visible, paymentStatus.params?.id]);

  const closeModal = () => {
    toggleModal('paymentStatus', false);
    setResult('');
    form.resetFields();
  };

  const status = Form.useWatch('status', form);

  useEffect(() => {
    const adminPayment = appointment?.admin_payment;

    if (adminPayment?.file) {
      setResult(adminPayment.file);
    }

    form.setFieldsValue({
      status: adminPayment?.paid ? 1 : 0,
      transfer: adminPayment?.transfer || 0,
      note: adminPayment?.note
    });
  }, [appointment, form, setResult]);

  const onFinish = async (values: {
    note: string;
    status: number;
    transfer: number;
  }) => {
    const id = paymentStatus.params?.id;
    if (!id) return;
    const paid = values.status === 1;

    try {
      setIsSending(true);
      await updateAppointmentDoctorPayment(id, {
        paid,
        transfer: paid ? Number(values.transfer || 0) : 0,
        payed_at: new Date().toISOString(),
        note: values.note,
        file: result || undefined
      });
      closeModal();
      // FinancialReports (REST since P13) listens to this modal's
      // visible falling edge to refetch; no extra refresh needed.
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setIsSending(false);
    }
  };

  const onUploadFile = async (e: UploadRequestOption) => {
    try {
      setResult('');
      setShowFileList(false);
      const res = await upload(e.file as File, {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onUploadProgress({ percent: value }) {
          setPercent(value);

          if (value === 100) {
            setTimeout(() => {
              setPercent(0);
            }, 100);
          }
        }
      });

      e.onSuccess?.(res);
    } catch (err) {
      e.onError?.(err as ProgressEvent<EventTarget>);
    } finally {
      setShowFileList(true);
    }
  };

  const isUploading = percent > 0;

  return (
    <Modal
      title="Payment status"
      open={paymentStatus.visible}
      onOk={appointment ? form.submit : closeModal}
      onCancel={closeModal}
      confirmLoading={isSending}
    >
      {isFetching ? (
        <Loading type="static" />
      ) : appointment ? (
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item label="Status" name="status" rules={[{ required: true }]}>
            <Select
              options={[
                {
                  label: 'Not paid',
                  value: 0
                },
                {
                  label: 'Paid',
                  value: 1
                }
              ]}
            />
          </Form.Item>
          {status === 1 && (
            <Form.Item
              label="Amount"
              name="transfer"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Form.Item label="Notes" name="note" rules={[{ required: false }]}>
            <TextArea style={{ height: 130 }} />
          </Form.Item>
          <Form.Item
            label="Receipt of payment"
            valuePropName="fileList"
            getValueProps={value => value}
            rules={[{ required: false }]}
          >
            <Upload.Dragger
              name="file"
              customRequest={onUploadFile}
              onRemove={f => onCancel(f.uid)}
              maxCount={1}
              disabled={isUploading}
              showUploadList={showFileList}
              fileList={
                result
                  ? [
                      {
                        uid: result,
                        name: result,
                        url: getFileUrl(result)
                      }
                    ]
                  : []
              }
              listType="picture"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
            </Upload.Dragger>
          </Form.Item>
          {isUploading && <Loading type="static" percent={percent} />}
        </Form>
      ) : (
        <Result
          status="error"
          title="Oops"
          subTitle="We've detected a potential issue with this appointment. Please review all the fields. Alternatively, if this is a paid session, please note that setting payment details is not applicable"
        />
      )}
    </Modal>
  );
};

export default memo(PaymentStatusModal);
